/**
 * E12-F05-T02 — какие поля есть у документа и как показать любое значение.
 *
 * ЗАЧЕМ ЭТОТ МОДУЛЬ СУЩЕСТВУЕТ. Промпты живут в таблице `ai_prompts` и правятся
 * клиентом в админке. Каждый раз, когда промпт менял схему вывода, рендереры
 * оставались со снимком СТАРЫХ имён полей — и молча выбрасывали всё остальное.
 * Замер по живым артефактам dev 2026-07-31 (`scripts/measure-doc-loss.mjs`):
 *
 *   · Interview Questions у 2 проектов из 8 рендерились в ПУСТОЙ документ —
 *     промпт вернул `opening_icebreaker` / `technical_professional` /
 *     `experience_achievements` / `role_specific_scenarios`, а рендерер знал
 *     только `technical` / `behavioral` / `culture_fit` / `client_specific`;
 *   · Sourcing Sheet терял 4–12 заполненных полей у ВСЕХ 8 — спецификация ждала
 *     `skillsets_priority_1..3`, `experience_ideal`, `education_must`, а модель
 *     отдаёт `skillsets_required`, `years_of_experience`, `education`.
 *
 * Поэтому здесь два слоя, и второй важнее первого:
 *
 *   1. СПЕЦИФИКАЦИЯ — порядок чтения и человеческие подписи для полей, которые
 *      промпты отдают сегодня. Она задаёт вид документа.
 *   2. СТРАХОВКА — всё непустое, чего в спецификации нет, выводится следом с
 *      автоматической подписью. Незнакомое поле выглядит хуже знакомого, но
 *      выглядеть хуже несравнимо лучше, чем исчезнуть.
 *
 * Значения разбираются ПО ФОРМЕ, а не по именам вложенных ключей, и это не
 * перестраховка: в живых данных один и тот же блок приходит как
 * `priority_2` / `priority_2_secondary` / `priority_2_advantageous` /
 * `priority_2_strong_advantage`, а «вне диапазона» — как `out_of_range` и
 * `out_of_range_exclude`. Любой список точных под-ключей устарел бы к следующей
 * генерации.
 *
 * Модуль чистый и DOM-free: им пользуются и HTML-экспорт (`docRender.ts`), и
 * просмотр на экране (`docViewModel.ts`) — тот же принцип, что у PK-QA-36.
 * Выход — HTML только из тегов allow-list'а (`richText.ts`), потому что просмотр
 * прогоняет его через санитайзер.
 */
import { esc } from './markdown.js';
import { isBlankListItem, looksLikeHtml, stripWrappingCodeFence } from './richText.js';
/**
 * Sourcing Sheet — поля в порядке чтения рекрутёром.
 *
 * Ключи взяты из ЖИВЫХ артефактов, а не из справочника n8n. Прежние
 * «спецификационные» имена (`skillsets_priority_1`, `experience_ideal`,
 * `education_must`, `location_priority_1`, `industry_ideal`, …) здесь
 * намеренно НЕ перечислены: в базе нет ни одного артефакта с ними, а пустая
 * строка на каждое такое имя — это те самые 19 пустых строк, из-за которых
 * настоящие данные было не найти. Если такой документ где-то всё же есть, его
 * поля выведет страховка (`resolveDocFields`), а не пустой шаблон.
 */
export const SOURCING_SHEET_FIELDS = [
    { key: 'position_title', label: 'Position' },
    { key: 'alternate_titles', label: 'Alternate titles' },
    { key: 'non_relevant_titles', label: 'Non-relevant titles' },
    { key: 'years_of_experience', label: 'Years of experience' },
    { key: 'education', label: 'Education' },
    { key: 'skillsets_required', label: 'Skillsets' },
    { key: 'relevant_keywords', label: 'Keywords' },
    {
        key: 'do_not_list_with_keywords',
        label: 'Exclude keywords',
        aliases: ['do_not_list_keywords', 'keywords_exclude'],
    },
    { key: 'industry', label: 'Industry' },
    { key: 'location', label: 'Location' },
    {
        key: 'competition_relevant_companies',
        label: 'Relevant / target companies',
        aliases: ['competition_and_target_companies', 'competition_companies'],
    },
    { key: 'dns_companies', label: 'DNS companies' },
    { key: 'dns_candidates', label: 'DNS candidates' },
    { key: 'client_name', label: 'Client' },
    { key: 'recruiter_name', label: 'Recruiter' },
    { key: 'brief_summary', label: 'Brief summary', block: true },
];
/**
 * Interview Questions — блоки вопросов в порядке хода интервью.
 *
 * Псевдонимы здесь несут вес: половина живых артефактов написана старым набором
 * имён (`technical`, `culture_fit`), половина — новым (`technical_professional`,
 * `culture_soft_skills`). Одна и та же секция, разные имена в разные месяцы.
 */
export const INTERVIEW_QUESTION_FIELDS = [
    { key: 'opening_icebreaker', label: 'Opening / Icebreaker' },
    { key: 'technical', label: 'Technical', aliases: ['technical_professional'] },
    { key: 'experience_achievements', label: 'Experience & Achievements' },
    { key: 'behavioral', label: 'Behavioral' },
    { key: 'role_specific_scenarios', label: 'Role-Specific Scenarios', aliases: ['situational'] },
    { key: 'culture_fit', label: 'Culture & Soft Skills', aliases: ['culture_soft_skills'] },
    { key: 'client_specific', label: 'Client-Specific' },
    { key: 'motivational', label: 'Motivational' },
    { key: 'closing', label: 'Closing', aliases: ['closing_candidate_questions'] },
];
/** `priority_1_must` → `Priority 1 must`: подпись для поля, о котором мы не знали. */
export function humanFieldLabel(key) {
    const words = key.replace(/[_-]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
/** Пустое значение: null / '' / [] / {} — то, что нечего показывать. */
export function isEmptyDocValue(value) {
    if (value == null)
        return true;
    if (Array.isArray(value))
        return value.every((v) => isEmptyDocValue(v));
    if (typeof value === 'object')
        return Object.values(value).every((v) => isEmptyDocValue(v));
    return String(value).trim().length === 0;
}
/**
 * Сопоставить спецификацию с содержимым артефакта.
 *
 * Сначала поля спецификации в её порядке — включая пустые, потому что пустое
 * поле и отсутствующий раздел это разные факты (KS3-07). Затем всё остальное
 * непустое, по алфавиту: страховка от следующей правки промпта.
 *
 * `skip` — ключи, у которых своё место в документе (заголовок JD, внутренние
 * комментарии): их не должно вынести страховкой обратно в текст.
 */
export function resolveDocFields(specs, content, skip = []) {
    const used = new Set(skip);
    const fields = [];
    for (const spec of specs) {
        const names = [spec.key, ...(spec.aliases ?? [])];
        const found = names.find((n) => n in content && !isEmptyDocValue(content[n]));
        for (const n of names)
            used.add(n);
        fields.push({
            key: found ?? spec.key,
            label: spec.label,
            block: spec.block === true,
            value: found ? content[found] : null,
            empty: found === undefined,
            known: true,
        });
    }
    for (const key of Object.keys(content).sort()) {
        if (used.has(key) || isEmptyDocValue(content[key]))
            continue;
        fields.push({
            key,
            label: humanFieldLabel(key),
            block: false,
            value: content[key],
            empty: false,
            known: false,
        });
    }
    return fields;
}
/**
 * Порядок под-ключей внутри вложенного значения.
 *
 * Сортировать их по алфавиту нельзя: `out_of_range` оказался бы перед
 * `possible`, а `non_relevant` — перед `ideal`, то есть исключения читались бы
 * раньше того, что ищем. Ранг определяется СЛОВАМИ в имени, а не точным именем,
 * потому что именно точные имена и меняются от генерации к генерации.
 */
function subKeyRank(key) {
    const k = key.toLowerCase();
    if (/priority_?1|(^|_)must|ideal|required/.test(k))
        return 0;
    if (/priority_?2|possible|also|advantage|secondary/.test(k))
        return 1;
    if (/priority_?3|nice|tertiary/.test(k))
        return 2;
    if (/non_?relevant|out_?of_?range|exclude|do_?not/.test(k))
        return 3;
    if (/^notes?$/.test(k))
        return 5;
    return 4;
}
function sortedSubKeys(obj) {
    return Object.keys(obj).sort((a, b) => subKeyRank(a) - subKeyRank(b) || a.localeCompare(b));
}
/** Ключи, под которыми модель кладёт сам текст пункта (остальное — пометки). */
const ITEM_TEXT_KEYS = ['question', 'text', 'body', 'value', 'title'];
/** Строка значения: готовый HTML пропускается, обычный текст экранируется (KS3-06). */
function textHtml(value) {
    const text = stripWrappingCodeFence(value).trim();
    if (!text)
        return '';
    return looksLikeHtml(text) ? text : esc(text);
}
/**
 * Один пункт списка. Объект — это вопрос со служебными пометками
 * (`assesses`, `red_flags`, `note`): сам вопрос идёт текстом, пометки — курсивной
 * строкой под ним. Выбрасывать их нельзя, это подсказки интервьюеру.
 */
function itemHtml(value) {
    if (value == null)
        return '';
    if (typeof value !== 'object')
        return textHtml(value);
    if (Array.isArray(value))
        return value.map(itemHtml).filter(Boolean).join('<br>');
    const obj = value;
    const textKey = ITEM_TEXT_KEYS.find((k) => typeof obj[k] === 'string' && obj[k] !== '');
    const head = textKey ? textHtml(obj[textKey]) : '';
    const notes = sortedSubKeys(obj)
        .filter((k) => k !== textKey && !isEmptyDocValue(obj[k]))
        .map((k) => `<em>${esc(humanFieldLabel(k))}: ${valueHtml(obj[k], { inline: true })}</em>`);
    if (!head && notes.length === 0)
        return '';
    return [head, ...notes].filter(Boolean).join('<br>');
}
/**
 * Любое значение артефакта → HTML из разрешённых тегов.
 *
 * Разбор идёт по форме значения: строка, список, вложенный объект, список
 * объектов-вопросов. Именно поэтому смена имён под-ключей в промпте ничего не
 * ломает — имена участвуют только в подписях и порядке.
 */
export function valueHtml(value, opts = {}) {
    if (isEmptyDocValue(value))
        return '';
    if (Array.isArray(value)) {
        // E03-F08-T06 — пустой/пробельный пункт не должен становиться маркером без текста.
        const items = value.map(itemHtml).filter((s) => !isBlankListItem(s));
        if (items.length === 0)
            return '';
        if (opts.inline)
            return items.join(', ');
        const tag = opts.ordered ? 'ol' : 'ul';
        return `<${tag}>${items.map((i) => `<li>${i}</li>`).join('')}</${tag}>`;
    }
    if (typeof value === 'object') {
        const obj = value;
        const parts = sortedSubKeys(obj)
            .filter((k) => !isEmptyDocValue(obj[k]))
            .map((k) => `<li><strong>${esc(humanFieldLabel(k))}</strong>: ${valueHtml(obj[k], { inline: true })}</li>`);
        return parts.length > 0 ? `<ul>${parts.join('')}</ul>` : '';
    }
    return textHtml(value);
}
/**
 * Значение поля для строки «подпись → значение».
 *
 * Плоский список строк читается в строке через запятую, структура — вложенным
 * списком. Решение принимается один раз здесь, потому что строку с полями рисуют
 * две поверхности (экспорт и просмотр), и разъехаться им нельзя.
 */
export function fieldValueHtml(value) {
    const structured = Array.isArray(value)
        ? value.some((v) => v !== null && typeof v === 'object')
        : value !== null && typeof value === 'object';
    return valueHtml(value, { inline: !structured });
}
/** Пункты списка по отдельности — для поверхности, которая строит список сама. */
export function valueItemsHtml(value) {
    if (isEmptyDocValue(value))
        return [];
    const items = Array.isArray(value) ? value.map(itemHtml) : [valueHtml(value)];
    return items.filter((s) => !isBlankListItem(s));
}
//# sourceMappingURL=docFields.js.map